import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Clock } from 'lucide-react';
import ImageArrayEditorContainer from '../../components/ImageArrayEditorContainer';
import RatingsSection from '../../components/RatingsSection';
import LocationSelector from '../../components/LocationSelector';
import ProfileEditor from './ProfileEditor';

// Profile stat keys to display in the stats section
const STAT_KEYS = ['age', 'height', 'method'];

/**
 * ProfileDesktop - Desktop view for own profile with two-column layout
 * Left: Image gallery in a rounded box
 * Right: Profile information with edit button
 */
function ProfileDesktop() {
  const ownProfile = useSelector((s) => s.user.profile);

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const firstTimeViews = ownProfile?.first_time_views || {};
  const isFirstTime = !firstTimeViews.profile_welcome;

  // Auto-enter edit mode on first-time setup
  useEffect(() => {
    if (isFirstTime) setEditing(true);
  }, [isFirstTime]);

  // Loading state
  if (!ownProfile) {
    return <div className="p-6 text-center text-sm text-muted-foreground">Loading profile...</div>;
  }

  // Show editor when editing
  if (editing) {
    return <ProfileEditor user={ownProfile} onSave={() => setEditing(false)} onCancel={() => setEditing(false)} />;
  }

  // Extract stats and images from user profile with fallbacks
  const stats = ownProfile?.stats ?? {};
  const images = ownProfile?.images ?? [];

  return (
    <div className="flex h-full gap-6 p-6 mt-10">
      {/* Left side - Image gallery in rounded box */}
      <div className="w-80 flex-shrink-0">
        <div className="aspect-square w-full rounded-2xl border border-border overflow-hidden bg-card flex items-center justify-center">
          <ImageArrayEditorContainer
            userId={ownProfile?.id}
            images={images}
            editable={false}
          />
        </div>
      </div>

      {/* Right side - Profile information */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex items-start justify-between mb-6">
          <div className="space-y-1">
            <h2 className="text-3xl font-semibold text-foreground">{ownProfile?.name || 'Unnamed'}</h2>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <LocationSelector location={ownProfile?.location} editable={false} />
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>Active recently</span>
              </div>
            </div>
          </div>

          {/* Edit button */}
          {!isFirstTime && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 rounded-full bg-primary/80 px-5 py-3 text-sm font-medium text-primary-foreground shadow-lg hover:bg-primary/90 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
              Edit
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Stats section - displays age, height, method in a grid */}
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">Stats</h3>
            <div className="grid grid-cols-3 gap-3">
              {STAT_KEYS.filter((k) => stats[k]).map((k) => (
                <div key={k} className="rounded-lg bg-secondary px-4 py-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{k}</div>
                  <div className="text-lg font-medium text-secondary-foreground">{stats[k]}</div>
                </div>
              ))}
            </div>
          </div>

          {/* About section - bio text */}
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">About</h3>
            <p className="text-base leading-relaxed text-foreground">{ownProfile?.bio || '—'}</p>
          </div>

          {/* Ratings section - shows user ratings/reviews */}
          <div>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">Reviews</h3>
            <RatingsSection userId={ownProfile?.id} userName={ownProfile?.name} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileDesktop;
